// Theme state, deliberately a plain module rather than a React context.
//
// AuthContext has to push the server's stored theme in as soon as
// /me/preferences resolves, and NavBar has to read and change it. Doing that
// through context would make the two providers' nesting order load-bearing for
// something that is really just one global value; a module sidesteps that.

export const THEMES = ['light', 'dark', 'system']
const STORAGE_KEY = 'ciTheme' // must match the pre-paint script in index.html

const listeners = new Set()
let current = readStored()

function readStored() {
  try {
    const t = localStorage.getItem(STORAGE_KEY)
    return THEMES.includes(t) ? t : 'system'
  } catch {
    return 'system' // private mode, or storage blocked
  }
}

function prefersDark() {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
}

// "system" writes no attribute at all, leaving the prefers-color-scheme rules
// in App.css to apply on their own. Setting data-theme="system" would match
// neither the light nor the dark selector and render the page unstyled.
function applyToDocument(theme) {
  const root = document.documentElement
  if (theme === 'light' || theme === 'dark') root.setAttribute('data-theme', theme)
  else root.removeAttribute('data-theme')
}

function notify() {
  listeners.forEach((fn) => fn())
}

export function getTheme() {
  return current
}

// The concrete appearance in force right now. CSS resolves "system" by itself,
// but JS cannot see that — and the charts need it, because Recharts takes
// colours as props rather than reading CSS custom properties.
export function getResolvedTheme() {
  return current === 'system' ? (prefersDark() ? 'dark' : 'light') : current
}

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// A user's explicit choice: apply and remember immediately, so the UI responds
// without waiting on the network. Persisting it is the caller's job.
export function setTheme(theme) {
  if (!THEMES.includes(theme)) return
  current = theme
  applyToDocument(theme)
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Storage unavailable — the choice still applies for this page.
  }
  notify()
}

// The stored value for the signed-in user. Server wins over whatever this
// browser remembered, so the same user does not see two different appearances
// depending on which device they last used (design D4 precedence).
export function applyServerTheme(theme) {
  if (!THEMES.includes(theme) || theme === current) return
  setTheme(theme)
}

export function nextTheme(theme) {
  return THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length]
}

// Keep resolved-theme consumers correct when the OS flips while a tab is open.
// Only meaningful while the choice is "system"; an explicit choice ignores it.
if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (current === 'system') notify()
  })
}

// Re-assert on load: the pre-paint script in index.html has already set the
// attribute, but this keeps the module and the DOM in agreement if storage was
// unreadable there and readable here, or vice versa.
if (typeof document !== 'undefined') applyToDocument(current)
